import {
    useCodesData,
    useContactsData, useFollowUpConfigData,
    useSupportRolesData,
    useSurveyData,
    useUserData,
    useUserSurveyData
} from "./SurveyView";
import {useIntl} from "react-intl";
import {formatDate} from "../Formats";
import {
    findUserById, getBranchFromCondition,
    getFollowUpOptions,
    getNameWithAnnieUser,
    getStudentFromContacts,
    getStudentName,
    reconstructConfigObject
} from "../DataFunctions";
import React, {useEffect, useState} from "react";
import {ReactComponent as MessageIcon} from "../svg/message.svg";
import {ReactComponent as SuccessIcon} from "../svg/success-big.svg";
import {ReactComponent as ErrorIcon} from "../svg/error-big.svg";
import {ReactComponent as InfoIcon} from "../svg/info-big.svg";
import {ReactComponent as AlertIcon} from "../svg/alert-big.svg";
import {ReactComponent as Chevron} from "../svg/chevron.svg";
import {getBranchIconColor, getMessageIcon, SlatSkeleton} from "../UIElements";
import {ReactComponent as SupportIcon} from "../svg/support.svg";
import {ReactComponent as ProviderIcon} from "../svg/provider.svg";
import _ from "lodash";
import {ReactComponent as BranchesIcon} from "../svg/branches.svg";
import {ReactComponent as Alert} from "../svg/alert.svg";
import {ReactComponent as BookmarkIcon} from "../svg/bookmark.svg";
import {ReactComponent as Info} from "../svg/info.svg";
import '../scss/SurveySummary.scss';
import {ReactComponent as TargetIcon} from "../svg/target.svg";
import {ReactComponent as FollowUpIcon} from "../svg/followup.svg";
import {GetUserAndRoleOfContact} from "../api/APISurvey";
import {ReactComponent as SnackSuccess} from "../svg/snack-success.svg";
import {useDataContext} from "./SurveyContextProvider";
import {getNodeColorForNested, RenderBranchError} from "./SurveyMessages";
import {getErrorsByBranch} from "../SurveyDataErrorChecks";
import styles from "./surveyEditor/messagesTab/SaveResponse/SaveResponse.module.scss";

export function SurveySummary({setSurveyChecked}) {
    const getUserAndRoleOfContactData = GetUserAndRoleOfContact()
    const loading = getUserAndRoleOfContactData.isLoading
    const error = getUserAndRoleOfContactData.isError
    const success = getUserAndRoleOfContactData.isSuccess

    if (success) {
       return <SurveySummaryDataLayer setSurveyChecked={setSurveyChecked} userRoleContactData={getUserAndRoleOfContactData?.data}/>
    }
    if (error) {
        return <SurveySummaryError />
    }
    return <SurveySummarySkeleton />
}

function SurveySummaryError() {
    const intl = useIntl()
    return <div className={"survey-summary-container"}>
        <h2 className={"error"}>
            {intl.formatMessage(
                {
                    id: 'overview.loadingFailed',
                    defaultMessage: 'Loading required info failed. Please try again.',
                })}
        </h2>
    </div>
}

function SurveySummarySkeleton() {
    const intl = useIntl()
    return <div className={"survey-summary-container"}>
        <h3>{intl.formatMessage(
            {
                id: 'overview.checkingSurvey',
                defaultMessage: 'Checking survey...',
            })}</h3>
        <div>
            <SlatSkeleton />
            <SlatSkeleton />
            <SlatSkeleton />
            <SlatSkeleton />
            <SlatSkeleton />
            <SlatSkeleton />
            <SlatSkeleton />
            <SlatSkeleton />
        </div>
    </div>
}

function SurveySummaryDataLayer({setSurveyChecked, userRoleContactData}) {
    const { value: surveyData } = useDataContext('surveyData')
    const { value: userSurveyData } = useDataContext('userSurveyData')
    const { value: categoryData } = useDataContext('categoryData')
    const { value: followUpConfigData } = useDataContext('followUpConfigData')
    const reconstructedSurveyData = reconstructConfigObject(surveyData)

    const supportBotSurvey = surveyData?.config?.type === "SUPPORTBOT"
    const customInstructionsForBot = surveyData?.config?.useCustomInstructions

    return <SurveySummaryContent
        setSurveyChecked={setSurveyChecked}
        userRoleContactData={userRoleContactData}
        surveyData={reconstructedSurveyData}
        userSurveyData={userSurveyData}
        categoryData={categoryData}
        followUpConfigData={followUpConfigData}
        supportBotSurvey={supportBotSurvey}
        customInstructionsForBot={customInstructionsForBot}
    />
}

function SurveySummaryContent({setSurveyChecked, userRoleContactData, surveyData, userSurveyData, categoryData, followUpConfigData, supportBotSurvey, customInstructionsForBot}) {
    const intl = useIntl()
    const { value: supportBots, setValue: setSupportBots } = useDataContext('supportBots');
    const surveyName = surveyData.config.title
    let startTime, endTime
    switch (intl.locale) {
        case "en":
            startTime = new Date(formatDate(surveyData.starttime)).toLocaleString("en-US")
            endTime = new Date(formatDate(surveyData.endtime)).toLocaleString("en-US")
            break
        case "fi":
            startTime = new Date(formatDate(surveyData.starttime)).toLocaleString("fi")
            endTime = new Date(formatDate(surveyData.endtime)).toLocaleString("fi")
            break
        default:
            startTime = new Date(formatDate(surveyData.starttime)).toLocaleString("en-US")
            endTime = new Date(formatDate(surveyData.endtime)).toLocaleString("en-US")
    }
    const hasReminders = surveyData.config.hasOwnProperty("reminders") && surveyData.config.reminders.length > 0

    const limitedEditing = surveyData.status === "IN PROGRESS" || surveyData.status === "FINISHED" || surveyData.status === "ARCHIVED"
    const surveyFinished = surveyData.status === "FINISHED" || surveyData.status === "ARCHIVED"
    const surveyArchived = surveyData.status === "ARCHIVED"

    const { value: branchOptionErrors } = useDataContext('branchOptionErrors')
    const branchOptionsErrors = !Object.keys(branchOptionErrors).every(key => Array.isArray(branchOptionErrors[key]) && branchOptionErrors[key].length === 0 )
    const errorMessageErrors = false
    const branchErrors = branchOptionsErrors || errorMessageErrors
    const branchErrorText = `${intl.formatMessage({
        defaultMessage:'Check for possible errors in',
        id:'checkForPossibleErrors'
    })}${branchOptionsErrors ? ' ' + intl.formatMessage({
        defaultMessage:'answer options',
        id:'checkForPossibleErrors.answerOptions'
    }): ''}${branchOptionsErrors && errorMessageErrors ? ' ' + intl.formatMessage({
        defaultMessage: 'and',
        id: 'and'
    }): ''}${errorMessageErrors ? ' ' + intl.formatMessage({
        defaultMessage:'error messages',
        id:'checkForPossibleErrors.errorMessages'
    }): ''}.`

    const supportBotsHeader = customInstructionsForBot ? intl.formatMessage({
        id: 'botInstructions',
        defaultMessage: 'Bot Instructions'
    }) : intl.formatMessage({
        id: 'supportBots',
        defaultMessage: 'Support Bots',
    })

    const surveySupportBots =  customInstructionsForBot ? intl.formatMessage(
        {
            id:'botHasInstructions',
            defaultMessage: 'The bot has instructions'
        }
    ) :`${intl.formatMessage({
        defaultMessage: 'Support Bots',
        id: 'supportBots'
    })}: ${surveyData?.config?.supportBots?.length} `

    const surveySupportBotsErrorText = customInstructionsForBot ? intl.formatMessage(
        {
            id: 'missingBotInstructions',
            defaultMessage: 'Missing bot instructions',
        }) : intl.formatMessage(
        {
            id: 'survey.overview.noSupportBots',
            defaultMessage: 'No support bots selected',
        })

    let startTimeErrorText
    const startTimeCheck = () => {
        if (!surveyData.hasOwnProperty("starttime") || surveyData.starttime.length < 1) {
            startTimeErrorText = intl.formatMessage(
                {
                    id: 'survey.overview.noStartTime',
                    defaultMessage: 'Missing start time',
                })
            return false
        }
        if (!limitedEditing) {
            if (new Date(formatDate(surveyData.starttime)) < new Date()) {
                startTimeErrorText = intl.formatMessage(
                    {
                        id: 'survey.overview.startTimeError',
                        defaultMessage: 'The start time is set in the past',
                    })
                return false
            }
        }
        return true
    }

    let endTimeErrorText
    const endTimeCheck = () => {
        if (!surveyData.hasOwnProperty("endtime") || surveyData.endtime.length < 1) {
            endTimeErrorText = intl.formatMessage(
                {
                    id: 'survey.overview.noEndTime',
                    defaultMessage: 'Missing end time',
                })
            return false
        }
        if (!surveyFinished) {
            if (new Date(formatDate(surveyData.endtime)) < new Date()) {
                endTimeErrorText = intl.formatMessage(
                    {
                        id: 'survey.overview.endTimeBeforeNow',
                        defaultMessage: 'The end time is set in the past',
                    })
                return false
            }
            if (new Date(formatDate(surveyData.endtime)) < new Date(formatDate(surveyData.starttime))) {
                endTimeErrorText = intl.formatMessage(
                    {
                        id: 'survey.overview.endTimeBeforeStartTime',
                        defaultMessage: 'The end time is set earlier than the start time',
                    })
                return false
            }
        }
        return true
    }

    let reminderErrorText
    const infoErrorCheck = () => {
        if (!surveyFinished) {
            if (hasReminders) {
                if (surveyData.config.reminders.some(reminder => reminder.delay < 1)) {
                    reminderErrorText = intl.formatMessage(
                        {
                            id: 'survey.overview.reminderDelayError',
                            defaultMessage: 'A reminder is set earlier than the survey start time',
                        })
                    return false
                }
                if (surveyData.config.reminders.some(reminder => reminder.message.length < 1)) {
                    reminderErrorText = intl.formatMessage(
                        {
                            id: 'survey.overview.reminderMessageError',
                            defaultMessage: 'A reminder is missing a message text',
                        })
                    return false
                }
            }
        }
        return true
    }
    const remindersLeft = hasReminders && surveyData.config.reminders.length === 1 ?
        intl.formatMessage(
            {
                id: 'survey.overview.reminderSet',
                defaultMessage: 'Reminder set',
            }) :
        intl.formatMessage(
            {
                id: 'survey.overview.remindersSet',
                defaultMessage: 'Reminders set',
            })
    const reminderInfoText = hasReminders ? surveyData.config.reminders.length + " " + remindersLeft : "0" + " " + remindersLeft

    function someMessageIsMissingText(data) {
        if (data.message.length < 1) {
            return true
        }
        const branchesAndOther = Object.keys(data).filter(key => key.startsWith('branch') || key === 'other')
        if (branchesAndOther.some(obj => data[obj].message.length < 1)) {
            return true
        }
        return branchesAndOther.some(obj => someMessageIsMissingText(data[obj]))
    }

    function variableMisspelled(data) {
        if (data.message.includes("{") || data.message.includes("}")) {
            if (checkForVariableTypingAndExtraBrackets(intl.formatMessage(({id:'survey.firstMessage',defaultMessage:'First message'})), data.message)) {
                return true
            }
        }
        const branchesAndOther = Object.keys(data).filter(key => key.startsWith('branch') || key === 'other')
        for (const obj of branchesAndOther) {
            if (data[obj].message.includes("{") || data[obj].message.includes("}")) {
                if (checkForVariableTypingAndExtraBrackets(obj, data[obj].message)) {
                    return true
                }
            }
        }
        return branchesAndOther.some(obj => variableMisspelled(data[obj]))
    }

    let falseBranch = ""
    function checkForVariableTypingAndExtraBrackets(branch, messageContent) {
        const correctVariableRegex = /{{[A-Za-z0-9]*}}/g
        const variables = [...messageContent.matchAll(correctVariableRegex)]
        if (variables.length < 1) {
            falseBranch = branch.startsWith("branch") ? branch.slice(6) : branch
            return true
        }
        const numberOfForwardBrackets = messageContent.match(/{/g).length
        const numberOfBackwardBrackets = messageContent.match(/}/g).length
        if (numberOfForwardBrackets !== variables.length * 2 || numberOfBackwardBrackets !== variables.length * 2) {
            falseBranch = branch.startsWith("branch") ? branch.slice(6) : branch
            return true
        }
    }

    const { value: contactsData } = useDataContext('contactsData')
    let contactHeaders = new Set()
    for (let i = 0; i < contactsData.length; i++) {
        if (contactsData[i].hasOwnProperty("meta")) {
            for (const key of Object.keys(contactsData[i]["meta"])) {
                contactHeaders.add(key)
            }
        }
    }
    contactHeaders = Array.from(contactHeaders)

    function variableMissing(data) {
        if (data.message.includes("{") || data.message.includes("}")) {
            if (checkIfVariableExists(data.message)) {
                return true
            }
        }
        const branchesAndOther = Object.keys(data).filter(key => key.startsWith('branch') || key === 'other')
        for (const obj of branchesAndOther) {
            if (data[obj].message.includes("{") || data[obj].message.includes("}")) {
                if (checkIfVariableExists(data[obj].message)) {
                    return true
                }
            }
        }
        return branchesAndOther.some(obj => variableMissing(data[obj]))
    }

    let falseVariable = ""
    function checkIfVariableExists(messageContent) {
        const correctVariableRegex = /{{[A-Za-z0-9]*}}/g
        const variables = [...messageContent.matchAll(correctVariableRegex)]
        for (const variable of variables) {
            let variableName = variable[0].slice(2, -2)
            if (!contactHeaders.includes(variableName)) {
                falseVariable = variableName
                return true
            }
        }
    }

    let messagesErrorText
    const messagesErrorCheck = () => {
        if (!surveyFinished) {
            if (someMessageIsMissingText(surveyData.config)) {
                messagesErrorText = intl.formatMessage(
                    {
                        id: 'survey.overview.messagesMissingText',
                        defaultMessage: 'A message is missing text',
                    })
                return false
            }
            if (variableMisspelled(surveyData.config)) {
                messagesErrorText = intl.formatMessage(
                    {
                        id: 'survey.overview.messagesVariableMisspelled',
                        defaultMessage: 'Invalid form of variable in:',
                    }) + " " + falseBranch
                return false
            }
            if (variableMissing(surveyData.config)) {
                messagesErrorText = intl.formatMessage(
                    {
                        id: 'survey.overview.messagesVariableMissing',
                        defaultMessage: 'Used variable does not exist:',
                    }) + " " + falseVariable
                return false
            }
        }
        return true
    }

    function supportNeedMissingProvider(data) {
        const branches = Object.keys(data).filter(key => key.startsWith('branch') || key === 'other')
        if (branches.some(obj => data[obj].hasOwnProperty("supportneed") && data[obj].supportneed && data[obj]?.supporttype === "SUPPORTPROVIDER" && !userSurveyData.some(annieuser => annieuser.meta.hasOwnProperty("category") && annieuser.meta.category.hasOwnProperty(data[obj].category)))) {
            return true
        }
        return branches.some(obj => supportNeedMissingProvider(data[obj]))
    }

    function someBranchIsMissingSupportCategory(data, categoryData) {
        const branches = Object.keys(data).filter(key => key.startsWith('branch') || key === 'other')
        if (branches.some(obj => data[obj].hasOwnProperty("supportneed") && data[obj].supportneed && !categoryData.hasOwnProperty(data[obj].category))) {
            return true
        }
        return branches.some(obj => someBranchIsMissingSupportCategory(data[obj], categoryData))
    }

    let supportNeedsErrorText
    const supportNeedsErrorCheck = () => {
        if (someBranchIsMissingSupportCategory(surveyData.config, categoryData) && supportNeedMissingProvider(surveyData.config)) {
            supportNeedsErrorText = intl.formatMessage({
                id:'survey.overview.supportNeedMissingTopic',
                defaultMessage: 'A support need is missing a topic'
            }) + ' & ' + intl.formatMessage({
                id:'survey.overview.supportProvidersMissing',
                defaultMessage:'Unassigned support needs'
            })
            return false
        }
        if (someBranchIsMissingSupportCategory(surveyData.config, categoryData)) {
            supportNeedsErrorText = intl.formatMessage({
                id:'survey.overview.supportNeedMissingTopic',
                defaultMessage: 'A support need is missing a topic'
            })
            return false
        }
        if (supportNeedMissingProvider(surveyData.config)) {
            supportNeedsErrorText = intl.formatMessage({
                id:'survey.overview.supportProvidersMissing',
                defaultMessage:'Unassigned support needs'
            })
            return false
        }
        return true
    }

    const followUpOptionsAndNoFollowup = getFollowUpOptions(followUpConfigData).concat(null)//followUpConfigData.concat(null)
    function someBranchUsesNotInUseFollowup(data) {
        const branches = Object.keys(data).filter(key => key.startsWith('branch') || key === 'other')
        if (branches.some(obj => data[obj].hasOwnProperty("followuptype") && !followUpOptionsAndNoFollowup.includes(data[obj].followuptype))) {
            return true
        }
        return branches.some(obj => someBranchUsesNotInUseFollowup(data[obj]))
    }

    const followUpTemplatesCheck = () => {
        return !someBranchUsesNotInUseFollowup(surveyData.config)
    }

    function someBranchHasFollowupSet(data) {
        const branches = Object.keys(data).filter(key => key.startsWith('branch') || key === 'other')
        if (branches.some(obj => data[obj].hasOwnProperty("followuptype") && (data[obj].followuptype !== null && data[obj].followuptype !== "NOFOLLOWUP"))) {
            return true
        }
        return  branches.some(obj => someBranchHasFollowupSet(data[obj]))
    }
    const noFollowUpsSet = !someBranchHasFollowupSet(surveyData.config)

    const surveyCoordinators = userSurveyData.filter(user => user.meta.coordinator)
    const coordinatorsSet = surveyCoordinators.length === 1 ?
        intl.formatMessage(
            {
                id: 'survey.overview.coordinatorSet',
                defaultMessage: 'Coordinator set',
            }) :
        intl.formatMessage(
            {
                id: 'survey.overview.coordinatorsSet',
                defaultMessage: 'Coordinators set',
            })
    const surveyCoordinatorsText = surveyCoordinators.length > 0 ? surveyCoordinators.length + " " + coordinatorsSet : "0" + " " + coordinatorsSet

    let notAvailableRecipients = []
    let duplicatePhoneNumbers = []
    let recipientsErrorText

    function recipientsDuplicatePhoneNumber() {
        const surveyContacts = surveyData.contacts;

        // Array to store contacts not found in contactsData
        const missingContacts = []
        const duplicates = new Set()

        const phoneNumbersSeen = new Set();
        const surveyPhoneNumbers = surveyContacts && surveyContacts.map(student => {
            const contact = getStudentFromContacts(student, contactsData)
            if (!contact) {
                missingContacts.push(student)
                return null
            }

            const phoneNumber = contact.meta?.phonenumber
            if (phoneNumbersSeen.has(phoneNumber)) {
                duplicates.add(phoneNumber)
            } else {
                phoneNumbersSeen.add(phoneNumber)
            }
            return phoneNumber
        })

        if (missingContacts.length > 0) {
            // Found contacts that aren't present in contactsData
            recipientsErrorText = intl.formatMessage({
                id: 'survey.overview.missingRecipients',
                defaultMessage: `Some selected recipients are no longer available.`
            })
            notAvailableRecipients = missingContacts
        }

        if (duplicates.size > 0) {
            duplicatePhoneNumbers = [...duplicates]
        }

        if (duplicates.size > 0 || missingContacts.length > 0) {
            return true
        }

        if (!surveyPhoneNumbers) {
            return false
        }

        return false
    }
    const recipientsErrorCheck = () => {
        if (!limitedEditing) {
            if (!surveyData.hasOwnProperty("contacts") || !surveyData.contacts || (surveyData.hasOwnProperty("contacts") && surveyData.contacts && surveyData.contacts.length < 1)) {
                recipientsErrorText = intl.formatMessage({
                    id: 'survey.overview.noRecipients',
                    defaultMessage: 'Survey cant be published without recipients'
                })
                return false
            }
            if (recipientsDuplicatePhoneNumber()) {
                if (!recipientsErrorText) {
                    recipientsErrorText = intl.formatMessage({
                        id: 'survey.overview.recipientsPhoneDuplicate',
                        defaultMessage: 'Survey has duplicate phone numbers'
                    })
                }

                return false
            }
        }
        return true
    }

    const { value: supportRolesData } = useDataContext('supportRolesData')
    function loopBranchesForSupportTypes(data) {
        let supportTypesArray = []
        branchSupportTypeLoop(data)
        function branchSupportTypeLoop(data) {
            for (const obj in data) {
                if (data[obj]?.supportneed) {
                    const supportType = data[obj]?.supporttype
                    supportType && supportTypesArray.indexOf(supportType) === -1 && supportTypesArray.push(supportType)
                }
                if (typeof data[obj] === 'object' && data[obj] !== null) {
                    branchSupportTypeLoop(data[obj])
                }
            }
        }
        return supportTypesArray
    }
    const supportTypes = loopBranchesForSupportTypes(surveyData?.config)
    const getSupportBotRoles = () => {
        if (!surveyData?.config?.supportBots || !Array.isArray(surveyData.config.supportBots) || !supportBots) {
            return []
        }

        const supportBotRoles = []
        surveyData.config.supportBots.forEach(botId => {
            const bot = supportBots.find(b => b.id === botId);
            if (bot && bot.config?.supportRequestTarget) {
                if (!supportBotRoles.includes(bot.config.supportRequestTarget)) {
                    supportBotRoles.push(bot.config.supportRequestTarget);
                }
            }
        });

        return supportBotRoles;
    }
    const roleSupportType = supportBotSurvey ? getSupportBotRoles() : supportTypes.filter(supportType => supportRolesData.includes(supportType))
    const supportNeedsSetToRoles = roleSupportType?.length > 0

    const recipients = surveyData?.contacts
    let recipientsWithRolesNotSet = []
    const recipientsRolesCheck = () => {
        if (supportNeedsSetToRoles) {
            for (let i = 0; i < recipients?.length; i++) {
                const recipient = recipients[i]
                for (let i = 0; i < roleSupportType?.length; i++) {
                    const role = roleSupportType[i]
                    if (!userRoleContactData.find(obj => obj?.contact === recipient && obj?.role === role)) {
                        recipientsWithRolesNotSet.push({
                            role: role,
                            contact: recipient
                        })
                    }
                }
            }
        }
        return recipientsWithRolesNotSet?.length < 1
    }

    const surveyRecipientsText = surveyData.hasOwnProperty("contacts") && surveyData.contacts ?
        surveyData.contacts.length + " " + intl.formatMessage({id:'survey.overview.recipients',defaultMessage:'Recipients'}) :
        "0 " + intl.formatMessage({id:'survey.overview.recipients',defaultMessage:'Recipients'})

    const surveyStatus = {
        name: surveyName.length > 0,
        startTime: startTimeCheck(),
        endTime: endTimeCheck(),
        reminders: infoErrorCheck(),
        messages: messagesErrorCheck(),
        supportNeeds: supportNeedsErrorCheck() || surveyArchived || supportBotSurvey,
        followUpTemplates: followUpTemplatesCheck() || surveyArchived || supportBotSurvey,
        coordinators: surveyCoordinators.length > 0 || surveyArchived,
        recipients: recipientsErrorCheck(),
        roleCheck: recipientsRolesCheck() || surveyArchived || customInstructionsForBot,
        supportBots: supportBotSurvey ? customInstructionsForBot ? surveyData?.config?.customInstructions?.length > 0 : surveyData?.config?.supportBots?.length > 0 : true
    }

    // Update parents survey checked state with status check
    useEffect(()=>{
        setSurveyChecked(!Object.keys(surveyStatus).some(obj => surveyStatus[obj] === false))
    },[])

    return <div className={"survey-summary-container"}>
        <Slat
            header={intl.formatMessage(
                {
                    id: 'survey.overview.surveyName',
                    defaultMessage: 'Survey Name',
                })}
            text={surveyName}
            success={surveyStatus.name}
            errorText={intl.formatMessage(
                {
                    id: 'survey.overview.missingName',
                    defaultMessage: 'Survey is missing name',
                })}
            disabled={false}
        />
        <Slat
            header={intl.formatMessage(
                {
                    id: 'survey.information.startTime',
                    defaultMessage: 'Start time',
                })}
            text={startTime}
            success={surveyStatus.startTime}
            errorText={startTimeErrorText}
            disabled={limitedEditing}
        />
        <Slat
            header={intl.formatMessage(
                {
                    id: 'survey.information.endTime',
                    defaultMessage: 'End time',
                })}
            text={endTime}
            success={surveyStatus.endTime}
            errorText={endTimeErrorText}
            disabled={surveyFinished}
        />
        <Slat
            header={intl.formatMessage(
                {
                    id: 'survey.navigation.reminders',
                    defaultMessage: 'Reminders',
                })}
            text={reminderInfoText}
            info={!hasReminders}
            success={surveyStatus.reminders}
            errorText={reminderErrorText}
            disabled={surveyFinished}
            expandable={hasReminders}
        >
            {hasReminders &&
            <ul>
                {surveyData.config.reminders.map( (reminder, i) => {
                    const startTime = new Date(formatDate(surveyData.starttime))
                    const reminderDate = new Date(startTime.getTime() + reminder.delay*60*60*1000)
                    const reminderTime = intl.locale === "fi" ? reminderDate.toLocaleString("fi") : reminderDate.toLocaleString("en-US")
                    return <li className={"reminder"} key={i}>
                        <b>
                            {reminder.delay}
                            {" "}
                            {intl.formatMessage(
                                {
                                    id: 'survey.reminders.hours',
                                    defaultMessage: 'hours',
                                })}
                        </b>
                        {" "}
                        {intl.formatMessage(
                            {
                                id: 'survey.reminders.afterLaunch',
                                defaultMessage: 'after survey start',
                            })}
                        <span>
                    {" → "}
                            {reminderTime}
                    </span>
                        <div className={"message"}>
                            <MessageIcon />
                            {reminder.message.length > 0 ?
                                reminder.message :
                                <span className={"alert"}>
                                {intl.formatMessage(
                                    {
                                        id: 'survey.overview.reminderMessageMissing',
                                        defaultMessage: 'Missing message text',
                                    })}
                            </span>
                            }
                        </div>
                    </li>
                })}
            </ul>
            }
        </Slat>
        <Slat
            header={intl.formatMessage({
                id: 'survey.navigation.messages',
                defaultMessage: 'Messages',
            })}
            text={supportBotSurvey ? intl.formatMessage({
               id: 'survey.overview.initialMessageInfoText',
               defaultMessage: 'Click to view the initial message'
            }) : !(branchErrors && !surveyStatus.messages) && intl.formatMessage({
                id: 'survey.overview.messagesInfoText',
                defaultMessage: 'Click to view message flow',
            })}
            success={surveyStatus.messages}
            errorText={messagesErrorText}
            disabled={surveyFinished}
            expandable={true}
            alert={branchErrors && !supportBotSurvey}
            alertText={branchErrorText}
        >
            <Messages
                surveyData={surveyData}
                supportBotSurvey={supportBotSurvey}
            />
        </Slat>
        {supportBotSurvey &&
            <Slat
                header={supportBotsHeader}
                text={(!customInstructionsForBot || surveyStatus.supportBots) ? surveySupportBots : ""}
                success={surveyStatus.supportBots}
                errorText={surveySupportBotsErrorText}
                disabled={false}
            />
        }
        {!supportBotSurvey &&
            <>
                <Slat
                    header={intl.formatMessage({
                        id:'survey.supportNeeds',
                        defaultMessage:'Support Needs'
                    })}
                    text={intl.formatMessage({
                        id:'survey.overview.supportNeedsText',
                        defaultMessage:'Click to view support needs'
                    })}
                    success={surveyStatus.supportNeeds}
                    errorText={supportNeedsErrorText}
                    disabled={surveyArchived}
                    expandable={true}
                >
                    <SupportNeeds
                        surveyData={surveyData}
                        categoryData={categoryData}
                        supportRolesData={supportRolesData}
                    />
                </Slat>
                <Slat
                    header={intl.formatMessage(
                        {
                            id: 'survey.overview.followUpTemplates',
                            defaultMessage: 'Follow ups',
                        })}
                    text={surveyStatus.followUpTemplates ? noFollowUpsSet ? intl.formatMessage(
                        {
                            id: 'survey.overview.followUpsNotSet',
                            defaultMessage: 'No follow ups have been set',
                        }) : intl.formatMessage(
                        {
                            id: 'survey.overview.followUpSuccess',
                            defaultMessage: 'Selected follow ups exist',
                        }) : ""}
                    info={noFollowUpsSet}
                    success={surveyStatus.followUpTemplates}
                    errorText={intl.formatMessage(
                        {
                            id: 'survey.overview.followUpFail',
                            defaultMessage: 'One or more follow ups are not in use',
                        })}
                    disabled={limitedEditing}
                />
            </>
        }
        <Slat
            header={intl.formatMessage(
                {
                    id: 'survey.supportNeeds.coordinators',
                    defaultMessage: 'Survey Coordinators',
                })}
            text={surveyCoordinatorsText}
            success={surveyStatus.coordinators}
            errorText={intl.formatMessage(
                {
                    id: 'survey.overview.missingCoordinator',
                    defaultMessage: 'Survey has to have a coordinator assigned',
                })}
            disabled={surveyArchived}
            expandable={surveyStatus.coordinators}
        >
            <SurveyCoordinator
            />
        </Slat>
        <Slat
            header={intl.formatMessage(
                {
                    id: 'survey.contacts.recipients',
                    defaultMessage: 'Survey Recipients',
                })}
            text={surveyRecipientsText}
            success={surveyStatus.recipients}
            errorText={recipientsErrorText}
            disabled={limitedEditing}
            expandable={!surveyData.hasOwnProperty("contacts") || (surveyData.contacts && !surveyData.contacts.length < 1)}
        >
            <RecipientsErrorText notAvailableRecipients={notAvailableRecipients} duplicatePhoneNumbers={duplicatePhoneNumbers} />
            <SurveyRecipients
                surveyData={surveyData}
                contactsData={contactsData}
            />
        </Slat>
        <Slat
            header={intl.formatMessage(
                {
                    id: 'overview.header.roleInfoRecipients',
                    defaultMessage: "Role info for recipients",
                })}
            text={customInstructionsForBot ?
                intl.formatMessage({
                    defaultMessage: 'Cross-check support request targets from bot instructions with recipient data manually',
                    id: 'overview.checkRolesFromInstructionsManually'
                }) :
                supportNeedsSetToRoles ?
                intl.formatMessage({
                    id:'overview.rolesForRecipientsText',
                    defaultMessage:'Click to view assigned roles'
                }) :
                intl.formatMessage({
                    id:'overview.noRolesUsed',
                    defaultMessage:'Support needs not assigned to roles'
                })}
            success={surveyStatus.roleCheck}
            info={customInstructionsForBot || !supportNeedsSetToRoles}
            errorText={intl.formatMessage(
                {
                    id: 'overview.recipientsMissingRoleInfo',
                    defaultMessage: 'Used roles not set for all recipients',
                })}
            disabled={surveyArchived}
            expandable={supportNeedsSetToRoles && !customInstructionsForBot}
        >
            <SupportNeedRoles
                roleSupportType={roleSupportType}
                recipientsWithRolesNotSet={recipientsWithRolesNotSet}
                contactsData={contactsData}
            />
        </Slat>
    </div>
}

function RecipientsErrorText({notAvailableRecipients = [], duplicatePhoneNumbers = []}) {
    const intl = useIntl()
    const maxDisplayCount = 10

    const formatArrayForDisplay = arr => {
        if (arr?.length > maxDisplayCount) {
            return `${arr.slice(0, maxDisplayCount).join(", ")}..`
        }
        return arr.join(", ")
    }
    const showNotAvailableRecipientsError = notAvailableRecipients.length > 0
    const showDuplicatePhoneNumbersError = duplicatePhoneNumbers.length > 0

    if (!showNotAvailableRecipientsError && !showDuplicatePhoneNumbersError) return null

    const joinedRecipients = showNotAvailableRecipientsError ? formatArrayForDisplay(notAvailableRecipients) : null
    const joinedNumbers = showDuplicatePhoneNumbersError ? formatArrayForDisplay(duplicatePhoneNumbers) : null

    return <>
        {showDuplicatePhoneNumbersError &&
            <p className={"error"}>
                {intl.formatMessage({
                    defaultMessage: 'The following numbers are duplicates: {numbers}.{br}Please address the issue to continue.',
                    id: 'duplicateNumbers.error'
                }, {
                    numbers: joinedNumbers,
                    br: <br/>
                })}
            </p>
        }
        {showNotAvailableRecipientsError &&
            <p className={"error"}>
                {intl.formatMessage({
                    defaultMessage: 'The following recipient IDs are not available: {recipients}.{br}Please remove them to publish the survey.',
                    id: 'recipientsNotAvailable.error'
                }, {
                    recipients: joinedRecipients,
                    br: <br/>
                })}
            </p>
        }
    </>
}

function SupportNeedRoles({roleSupportType, recipientsWithRolesNotSet, contactsData}) {
    const intl = useIntl()
    const tableData = [
        {
            header: intl.formatMessage({id:"assignedToRole", defaultMessage:"Assigned to role"}),
        },
        {
            header: intl.formatMessage({id: "recipientsMissingRoleInfo", defaultMessage: "Recipients missing role info"}),
        }
    ]

    return <table className={"summary-container"}>
        <thead>
        <tr>
            <th/>
            {tableData.map((obj,i) => {
                return <th key={i}>
                    {obj?.header}
                </th>
            })}
        </tr>
        </thead>
        <tbody>
        {roleSupportType.map((role,i)=> {
            return <tr key={i}>
                <td>{i+1}.</td>
                <td>{intl.formatMessage({id:"role."+role, defaultMessage:role})}</td>
                <td>
                    <NoRolesRecipients data={recipientsWithRolesNotSet.filter(contact => contact?.role === role)} contactsData={contactsData}/>
                </td>
            </tr>
        })}
        </tbody>
    </table>
}

function NoRolesRecipients({data, contactsData}) {
    const intl = useIntl()
    const [showAmount, setShowAmount] = useState(5)
    const getContact = contact => {
        const student = getStudentFromContacts(contact, contactsData)
        return getStudentName(student, intl)
    }
    if (data?.length < 1) {
        return <p className={"success"}><SnackSuccess />{intl.formatMessage({id: "noMissingRoleInfo", defaultMessage:"No missing role information"})}</p>
    }
    return <>
        {data.slice(0,showAmount).map((user,i)=>{
            return i === 0 ? getContact(user?.contact) : ", " + getContact(user?.contact)
        })}
        {data?.length > showAmount &&
        <button onClick={()=>setShowAmount(showAmount+5)}>+ {data?.length - showAmount} {intl.formatMessage({id:"survey.table.more", defaultMessage:"more"})}</button>
        }
        </>
}

export function Slat({header,text,errorText,success,info,infoText,expandable,disabled,children,alert,alertText}){
    const [isOpen, setIsOpen] = useState(false)
    let headerStatus = "success"
    let statusIcon = <SuccessIcon />
    if (!success) {
        headerStatus = "error"
        statusIcon = <ErrorIcon />
    }
    if (info) {
        headerStatus = "info"
        statusIcon = <InfoIcon />
    }
    if (success && alert) {
        headerStatus = "alert"
        statusIcon = <AlertIcon />
    }
    let slatClass = "slat"
    if (disabled) {slatClass = slatClass.concat(" disabled")}
    if (isOpen) {slatClass = slatClass.concat(" open")}

    return <div className={slatClass}>
        <div className={expandable ? "slat-header toggle" : "slat-header"} onClick={expandable ? ()=>setIsOpen(!isOpen):null}>
            {statusIcon}
            <h4 className={headerStatus}>{header}</h4>
            {text &&
            <p>→ {text}</p>
            }
            {!success &&
            <p className={"error"}>→ {errorText}</p>
            }
            {info && infoText &&
            <p className={"info"}>→ {infoText}</p>
            }
            {alert && alertText &&
                <p className={"alert"}>→ {alertText}</p>
            }
            {expandable &&
            <span className={isOpen? "chevron open" : "chevron"}>
                <Chevron />
            </span>
            }
        </div>
        {isOpen &&
        <div className={"slat-content"}>
            {children}
        </div>
        }
    </div>
}

function SurveyRecipients({surveyData, contactsData}) {

    const intl = useIntl()
    const surveyContacts = surveyData.contacts
    const surveyContactsDetails = surveyContacts && surveyContacts.map(student => getStudentFromContacts(student, contactsData))
    const surveyDegrees = surveyContactsDetails && surveyContactsDetails.map(student => student?.meta && student?.meta?.degree)
    const surveyGroups = surveyContactsDetails && surveyContactsDetails.map(student => student?.meta && student?.meta?.group)
    const surveyLocations = surveyContactsDetails && surveyContactsDetails.map(student => student?.meta && student?.meta?.location)
    const uniqueGroups = surveyGroups && surveyGroups.filter(filterUnique)
    const uniqueDegrees = surveyDegrees && surveyDegrees.filter(filterUnique)
    const uniqueLocations = surveyLocations && surveyLocations.filter(filterUnique)
    function filterUnique(value, index, self) {
        return self.indexOf(value) === index
    }

    const groupsWithAmounts = uniqueGroups.map(group => countAmounts(group, surveyGroups)).sort(amountSort)
    const degreesWithAmounts = uniqueDegrees.map(degree => countAmounts(degree, surveyDegrees)).sort(amountSort)
    const locationsWithAmounts = uniqueLocations.map(location => countAmounts(location, surveyLocations)).sort(amountSort)
    function countAmounts(obj, allObj) {
        let amount = 0
        for (let i = 0; i < allObj.length; i++) {
            if (allObj[i] === obj) {
                amount++
            }
        }
        if (!obj) {
            return {[intl.formatMessage(
                    {
                        id: 'survey.table.empty',
                        defaultMessage: '(Empty)',
                    })]: amount}
        }
        return {[obj]: amount}
    }
    function amountSort(a, b) {
        let first = a[Object.keys(a)[0]]
        let second = b[Object.keys(b)[0]]
        if ( first > second ){
            return -1
        }
        if ( first < second ){
            return 1
        }
        return 0
    }

    const summaryData = [
        {
            header: intl.formatMessage({id:"group", defaultMessage:"Group"}),
            keys: groupsWithAmounts
        },
        {
            header: intl.formatMessage({id:'degree', defaultMessage:'Degree'}),
            keys: degreesWithAmounts
        },
        {
            header: intl.formatMessage({id:'location', defaultMessage:'Location'}),
            keys: locationsWithAmounts
        }
    ]

    return <div>
        <h4>
            {intl.formatMessage(
                {
                    id: 'survey.contacts.summary',
                    defaultMessage: 'Summary',
                })}
        </h4>
        <table className={"summary-container recipient-overview"}>
            <thead>
            <tr>
                {summaryData.map((obj,i) => {
                    return <th key={i}>
                        {obj.header} ({obj.keys.length})
                    </th>
                })}
            </tr>
            </thead>
            <tbody>
            <tr>
                {summaryData.map((obj,i) => {
                    return <td key={i}>
                        <ol>
                            {obj.keys.map((key, i)=>{
                                return <li key={i}>
                                    {Object.keys(key)[0]} ({key[Object.keys(key)[0]]})
                                </li>
                            })}
                        </ol>
                    </td>
                })}
            </tr>
            </tbody>
        </table>
    </div>
}

function SurveyCoordinator() {
    const {value: userSurveyData } = useDataContext('userSurveyData')
    const {value: userData } = useDataContext('userData')
    const surveyCoordinators = userSurveyData.filter(user => user.meta.coordinator)

    return <ul>
        {surveyCoordinators.map( (coordinator, i) => {
            return <li className={"coordinator"} key={i}>
                <b>{findUserById(coordinator.annieuser, userData)}</b>
            </li>
        })}
    </ul>
}

function Messages({surveyData, supportBotSurvey}) {
    const intl = useIntl()
    const { value: branchOptionErrors } = useDataContext('branchOptionErrors')
    const branchErrors = getErrorsByBranch(branchOptionErrors, "initial")
    const hasErrors = !Object.keys(branchErrors).every(key => Array.isArray(branchErrors[key]) && branchErrors[key].length === 0 )

    const branches = Object.keys(surveyData.config).filter(key => key.startsWith('branch') || key === 'other').sort()
    const [minimize, setMinimize] = useState(false)
    const saveResponse = surveyData?.config?.response?.length > 0
    const hideToggle = supportBotSurvey

    if (supportBotSurvey) {
        return <div className={"messages-container summary-container"}>
            <div className={"message-group first"}>
                <div className={"message-card"}>
                    <div className={"message-header"}>
                        <div className={"icon"}>
                            <MessageIcon/>
                            {intl.formatMessage({id:'survey.firstMessage',defaultMessage:'First message'})}
                        </div>
                    </div>
                    <div className={"message-text"}>
                        {surveyData.config.message.length > 0 ?
                            surveyData.config.message :
                            <span className={"error"}>
                            {intl.formatMessage({id:"survey.overview.reminderMessageMissing", defaultMessage:"Missing message text"})}
                        </span>
                        }
                    </div>
                </div>
            </div>
        </div>
    }

    return <>
    {!hideToggle &&
        <div className={"button-switch"}>
            <button className={minimize ? "" : "active"} onClick={()=>setMinimize(false)}>
                {intl.formatMessage({id:'show', defaultMessage:'Show'})}
            </button>
            <button className={minimize ? "active" : ""} onClick={()=>setMinimize(true)}>
                {intl.formatMessage({id:'survey.overview.minimize', defaultMessage:'Hide content'})}
            </button>
        </div>
    }
        <div className={"messages-container summary-container"}>
            <div className={"message-group first"}>
                {minimize ?
                    <div className={"message-minimized"} title={surveyData.config.message}>
                        <div className={"icon"}>
                            <MessageIcon/>
                        </div>
                    </div>
                    :
                    <div className={"message-card"}>
                        <div className={"message-header"}>
                            <div className={"icon"}>
                                <MessageIcon/>
                                {intl.formatMessage({id:'survey.firstMessage',defaultMessage:'First message'})}
                            </div>
                        </div>
                        <div className={"message-text"}>
                            {surveyData.config.message.length > 0 ?
                                surveyData.config.message :
                                <span className={"error"}>
                            {intl.formatMessage({id:"survey.overview.reminderMessageMissing", defaultMessage:"Missing message text"})}
                        </span>
                            }
                        </div>
                        {saveResponse &&
                            <div className={"toolbar"}>
                                {saveResponse &&
                                    <div className={"selection-container"}>
                                        <BookmarkIcon />
                                        <p>
                                            {surveyData?.config?.response}
                                        </p>
                                    </div>
                                }

                            </div>
                        }
                    </div>
                }
                <RenderBranchError branch={"initial"} />
                {branches.length > 0 &&
                <div className={branches.length > 1 ? "message-children line" : "message-children"}>
                    {branches.sort((a, b) => {
                        // Put keys starting with "other" last
                        if (a.startsWith("other")) return 1
                        if (b.startsWith("other")) return -1

                        // Extract number from branch name for sorting
                        const extractNumber = (str) => {
                            const match = str.match(/(\D*)(\d+)/);
                            if (match) {
                                return {
                                    prefix: match[1].toLowerCase(),
                                    num: parseInt(match[2], 10),
                                }
                            }
                            return { prefix: str.toLowerCase(), num: -1 }
                        }

                        let conditionA = surveyData.config[a]?.condition ? surveyData.config[a]?.condition.replace(/^\^|\\b/g, '') : ''
                        let conditionB = surveyData.config[b]?.condition ? surveyData.config[b]?.condition.replace(/^\^|\\b/g, '') : ''

                        const numA = extractNumber(conditionA)
                        const numB = extractNumber(conditionB)

                        // Sort by branch prefix
                        if (numA.prefix < numB.prefix) {
                            return -1
                        } else if (numA.prefix > numB.prefix) {
                            return 1
                        }

                        // Sort by branch number
                        return numA.num - numB.num
                    })
                        .map((branch, i) => {
                        return <MessageChildren key={i} surveyData={surveyData} data={surveyData.config[branch]} branch={branch} minimize={minimize}/>
                    })}
                </div>
                }
            </div>
        </div>
    </>
}

function MessageChildren({data, branch, minimize, surveyData}) {
    const intl = useIntl()
    const { value: userSurveyData } = useDataContext('userSurveyData')
    const { value: userData } = useDataContext('userData')
    const { value: categoryData } = useDataContext('categoryData')
    const { value: supportRolesData } = useDataContext('supportRolesData')

    const { value: branchOptionErrors } = useDataContext('branchOptionErrors')
    const branchErrors = getErrorsByBranch(branchOptionErrors, branch)
    const hasErrors = !Object.keys(branchErrors).every(key => Array.isArray(branchErrors[key]) && branchErrors[key].length === 0 )

    const branchSymbol = branch === "other" || data?.condition === '.*'
        ? getMessageIcon(data, branch !== "other", branch)
        : getBranchFromCondition(data?.condition).toUpperCase()
    let branchText = ""
    if (branch === "other") {branchText = intl.formatMessage({id:'survey.message.other',defaultMessage:'Error message'})}
    if (data.condition === ".*") {branchText = intl.formatMessage({id:'survey.message.freeText',defaultMessage:'Free text response'})}
    const branches = Object.keys(data).filter(key => key.startsWith('branch') || key === 'other')
    const color = getNodeColorForNested(surveyData?.config, branch, branch !== "other")
    let style = {backgroundColor:color}
    const categoryProviders = userSurveyData.filter(user => user.meta.hasOwnProperty("category") && user.meta.category[data.category])
    const providerNames = categoryProviders.length > 0 ? categoryProviders.map(user => getNameWithAnnieUser(userData, user.annieuser)).join('\n') : intl.formatMessage({
        id:'survey.overview.supportProvidersMissing',
        defaultMessage: 'Unassigned support needs'
    })
    const supportNeed = data.hasOwnProperty("supportneed") && data.supportneed
    const saveResponse = data?.response?.length > 0
    const codeName = categoryData.hasOwnProperty(data.category) ? categoryData[data.category]["title"] : intl.formatMessage({id:'survey.overview.noSupportNeedAssigned',defaultMessage:'No support need assigned'})

    return <div className={"message-group"}>
        {minimize ?
            <div className={"message-minimized"} style={style} title={data.message}>
                <div className={"icon"}>
                    {branchSymbol}
                </div>
            </div>
            :
            <div className={"message-card"}>
                <div className={"message-header"} style={style}>
                    <div className={"icon"}>
                        {branchSymbol}
                        {branchText}
                    </div>
                </div>
                <div className={"message-text"}>
                    {data.message.length > 0 ?
                        data.message :
                        <span className={"error"}>
                            {intl.formatMessage({id:"survey.overview.reminderMessageMissing", defaultMessage:"Missing message text"})}
                        </span>
                    }
                </div>
                {(supportNeed || saveResponse) &&
                <div className={"toolbar"}>
                    {supportNeed &&
                        <>
                            <div className={"selection-container"}>
                                <SupportIcon />
                                <p className={categoryData.hasOwnProperty(data.category) ? "" : "alert"}>
                                    {codeName}
                                </p>
                            </div>
                            <div className={"selection-container"}>
                                <TargetIcon/>
                                <p>
                                    {supportRolesData.includes(data?.supporttype) ?
                                        intl.formatMessage({
                                            id: "role."+data?.supporttype,
                                            defaultMessage: data?.supporttype
                                        })
                                        :
                                        intl.formatMessage({
                                            id: "supporttype."+data?.supporttype,
                                            defaultMessage: data?.supporttype
                                        })
                                    }
                                </p>
                                {data?.supporttype === "SUPPORTPROVIDER" &&
                                    <div className={"selection-container"}>
                                        <ProviderIcon/>
                                        <p className={categoryProviders?.length > 0 ? "" : "alert"} title={providerNames}>
                                            {categoryProviders?.length}
                                        </p>
                                    </div>
                                }
                            </div>
                            <div className={"selection-container"}>
                                <FollowUpIcon/>
                                <p>
                                    {data?.followuptype ?
                                        intl.formatMessage({
                                            id: "followup."+data?.followuptype,
                                            defaultMessage: data?.followuptype
                                        })
                                        :
                                        intl.formatMessage({
                                            id: "followup.noFollowUp",
                                            defaultMessage: "No follow up"
                                        })
                                    }
                                </p>
                            </div>
                        </>
                    }
                    {supportNeed && saveResponse && <hr />}
                    {saveResponse &&
                        <div className={"selection-container"}>
                            <BookmarkIcon />
                            <p>
                                {data?.response}
                            </p>
                        </div>
                    }

                </div>
                }
            </div>
        }
        <RenderBranchError branch={branch} />

        { branches.length > 0 &&
        <div className={branches.length > 1 ? "message-children line" : "message-children"}>
            {branches.sort((a, b) => {
                // Put keys starting with "other" last
                if (a.startsWith("other")) return 1
                if (b.startsWith("other")) return -1

                // Extract number from branch name for sorting
                const extractNumber = (str) => {
                    const match = str.match(/(\D*)(\d+)/);
                    if (match) {
                        return {
                            prefix: match[1].toLowerCase(),
                            num: parseInt(match[2], 10),
                        }
                    }
                    return { prefix: str.toLowerCase(), num: -1 }
                }

                let conditionA = data[a]?.condition ? data[a]?.condition.replace(/^\^|\\b/g, '') : ''
                let conditionB = data[b]?.condition ? data[b]?.condition.replace(/^\^|\\b/g, '') : ''

                const numA = extractNumber(conditionA)
                const numB = extractNumber(conditionB)

                // Sort by branch prefix
                if (numA.prefix < numB.prefix) {
                    return -1
                } else if (numA.prefix > numB.prefix) {
                    return 1
                }

                // Sort by branch number
                return numA.num - numB.num
            })
                .map((branch, i) => {
                return <MessageChildren key={i} data={data[branch]} branch={branch} minimize={minimize} surveyData={surveyData}/>
            })}
        </div>
        }
    </div>
}

function SupportNeeds({ surveyData, categoryData, supportRolesData }) {
    const intl = useIntl()
    const supportNeeds = findBranchesWithSupportNeed(surveyData.config)
    const supportNeedGroups = _.groupBy(supportNeeds, need => `${need.category}-${need.supporttype}-${need.followup}`)

    function findBranchesWithSupportNeed(data) {
        let supportNeedBranches = []
        branchLoopCheck(data)
        function branchLoopCheck(data) {
            for (const obj in data) {
                if (data[obj]?.supportneed) {
                    supportNeedBranches.push(
                        {
                            branch: obj,
                            category: data[obj].category,
                            condition: data[obj].condition,
                            title:data[obj].message,
                            categoryName: categoryData.hasOwnProperty(data[obj].category) ?
                                categoryData[data[obj].category]["title"] :
                                intl.formatMessage({id:'survey.overview.noSupportNeedAssigned',defaultMessage:'No support need assigned'}),
                            supportNeedAssigned: categoryData.hasOwnProperty(data[obj].category),
                            supporttype: data[obj].supporttype,
                            followuptype: data[obj]?.followuptype ? data[obj].followuptype : "noFollowUp"
                        })
                }
                if (typeof data[obj] === 'object' && data[obj] !== null) {
                    branchLoopCheck(data[obj])
                }
            }
        }

        return supportNeedBranches
    }

    return <table className={"summary-container support-need-overview"}>
        <thead>
        <tr>
            <th/>
            <th>
                <SupportIcon/>
                {intl.formatMessage(
                    {
                        id: 'supportTopic',
                        defaultMessage: 'Support Topic',
                    })}
            </th>
            <th>
                <TargetIcon/>
                {intl.formatMessage({id:"assignedTo",defaultMessage:"Assigned to"})}
            </th>
            <th>
                <ProviderIcon/>
                {intl.formatMessage(
                    {
                        id: 'survey.supportNeeds.supportProvider',
                        defaultMessage: 'Support Provider',
                    })}
            </th>
            <th>
                <FollowUpIcon/>
                {intl.formatMessage(
                    {
                        id: 'survey.followUp',
                        defaultMessage: 'Follow Up',
                    })}
            </th>
            <th>
                <BranchesIcon/>
                {intl.formatMessage(
                    {
                        id: 'survey.message.option',
                        defaultMessage: 'Option',
                    })}
            </th>
        </tr>
        </thead>
        <tbody>
        {Object.keys(supportNeedGroups).map((group, i) => {
            const categoryObj = supportNeedGroups[group]
            return <tr key={i}>
                <td>{i+1}</td>
                <td className={"topics"}>
                    {categoryObj[0].supportNeedAssigned ?
                        categoryObj[0].categoryName :
                        <span className={"alert"}>
                            <Alert />
                            {categoryObj[0].categoryName}
                        </span>
                    }
                </td>
                <td>
                    {supportRolesData.includes(categoryObj[0].supporttype) ?
                        intl.formatMessage({
                            id: "role",
                            defaultMessage: "Role"
                        })
                    :
                        intl.formatMessage({
                            id: "supporttype."+categoryObj[0].supporttype,
                            defaultMessage: categoryObj[0].supporttype
                        })
                    }
                </td>
                <td className={"providers"}>
                    <SupportProviders data={categoryObj[0]}
                                      showRole={supportRolesData.includes(categoryObj[0].supporttype)}
                                      role={intl.formatMessage({
                                          id: "role."+categoryObj[0].supporttype,
                                          defaultMessage: categoryObj[0].supporttype
                                      })}
                    />
                </td>
                <td>
                    {intl.formatMessage({
                        id: "followup."+categoryObj[0].followuptype,
                        defaultMessage: categoryObj[0].followuptype
                    })}
                </td>
                <td className={"branches"}>
                    {categoryObj.map((obj, i) =>{
                        const branchSymbol = obj.branch === "other" || obj?.condition === '.*'
                            ? getMessageIcon(obj, obj.branch !== "other", obj.branch)
                            : getBranchFromCondition(obj?.condition).toUpperCase()
                        const color = getNodeColorForNested(surveyData?.config, obj.branch, obj.branch !== "other")
                        const style = {backgroundColor:color}
                        return <div key={i} className={"message-minimized"} title={obj.title} style={style}>
                            <div className={"icon"}>{branchSymbol}</div>
                        </div>
                    })}
                </td>
            </tr>
        })}
        </tbody>
    </table>
}

function SupportProviders({data, showRole, role}) {
    const { value: userSurveyData } = useDataContext('userSurveyData')
    const { value: userData } = useDataContext('userData')

    const categoryProviders = userSurveyData.filter(user => user.meta.hasOwnProperty("category") && user.meta.category[data.category])
    const intl = useIntl()

    return <ol>
        {showRole ?
            role :
            categoryProviders.length > 0 ?
                categoryProviders.map((provider, i) => {
                    return <li key={i}>
                        {findUserById(provider.annieuser, userData)}
                    </li>})
                :
                data?.supporttype === "SUPPORTPROVIDER" ?
                    <span className={"alert"} title={intl.formatMessage({
                        id: 'survey.overview.supportProvidersMissing',
                        defaultMessage: 'Unassigned support needs'
                    })}>
                <Alert />
                        {intl.formatMessage({
                            id: 'unassigned',
                            defaultMessage: 'Unassigned'
                        })}
            </span> : ""
        }
    </ol>
}